import { Box, Typography, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Vendor } from "@/types";
import { administratorChoiceHelperText, administratorRoleLabel, type VendorRoleMeta } from "./policyVendorSelector";
import { OTHER_VENDOR } from "./vendorAssignmentShared";

// =============================================================================
// Administrator choices (#7552)
// =============================================================================

export interface AdministratorChoicesProps {
  name: string;
  ariaLabel: string;
  title: string;
  choices: Vendor[];
  roleMeta: Map<string, VendorRoleMeta>;
  runoutCutover?: string;
  /** Selected vendor id, OTHER_VENDOR, or "" for none. */
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  /** Include the trailing "Different vendor…" radio. */
  withOther?: boolean;
  /** Toolbar variant: smaller title, horizontal rhythm. */
  dense?: boolean;
  /** Helper under "Different vendor…", e.g. "Search all TPA / Rx Vendors." (#7552 AC1). */
  otherHelper?: string;
}

/**
 * The policy's administrators as radios, each with its role and the rule as helper
 * text — the run-out banner, the "from policy" chip and the provenance caption,
 * folded into the option they describe (#7552).
 */
export const AdministratorChoices = ({
  name,
  ariaLabel,
  title,
  choices,
  roleMeta,
  runoutCutover,
  value,
  disabled,
  onChange,
  withOther,
  dense,
  otherHelper = "Search all vendors.",
}: AdministratorChoicesProps) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 600, mb: dense ? 0.25 : 0.5 }}
    >
      {title}
    </Typography>
    <RadioGroup
      name={name}
      aria-label={ariaLabel}
      value={value}
      onChange={(_e, v) => onChange(v)}
      row={dense}
      sx={dense ? { columnGap: 2 } : { rowGap: 0.5 }}
    >
      {choices.map((vendor) => {
        const meta = roleMeta.get(String(vendor.id));
        const role = meta ? administratorRoleLabel(meta) : null;
        const helper = meta ? administratorChoiceHelperText(meta, runoutCutover) : null;
        const helperId = `${name}-${vendor.id}-helper`;
        return (
          <FormControlLabel
            key={vendor.id}
            value={String(vendor.id)}
            disabled={disabled}
            sx={{ alignItems: "flex-start", mr: 0, ".MuiRadio-root": { pt: 0.25 } }}
            control={
              <Radio size="small" inputProps={helper && !dense ? { "aria-describedby": helperId } : undefined} />
            }
            label={
              <Box>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                  {vendor.company_name}
                </Typography>
                {role && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, fontWeight: 600, color: meta?.isRunout ? "warning.main" : "primary.main" }}
                  >
                    {role}
                  </Typography>
                )}
                {helper && !dense && (
                  <Typography id={helperId} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {helper}
                  </Typography>
                )}
              </Box>
            }
          />
        );
      })}
      {withOther && (
        <FormControlLabel
          value={OTHER_VENDOR}
          disabled={disabled}
          sx={{ alignItems: "flex-start", mr: 0, ".MuiRadio-root": { pt: 0.25 } }}
          control={<Radio size="small" />}
          label={
            <Box>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                Different vendor…
              </Typography>
              {!dense && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {otherHelper}
                </Typography>
              )}
            </Box>
          }
        />
      )}
    </RadioGroup>
  </Box>
);
